import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { CaseStudy as CaseStudyType } from '../data/CaseStudyData';
import Button from '../components/Button';
interface CaseStudyProps {
  caseStudy: CaseStudyType;
}
const CaseStudy = ({
  caseStudy
}: CaseStudyProps) => {
  // Fix navigation scrolling
  useEffect(() => {
    // Add smooth scrolling
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href');
        if (!targetId) return;
        const targetElement = document.querySelector(targetId);
        if (!targetElement) return;
        window.scrollTo({
          top: targetElement.getBoundingClientRect().top + window.scrollY - 100,
          behavior: 'smooth'
        });
      });
    });
    // Clean up
    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', function (e) {});
      });
    };
  }, []);
  return <div className="bg-gray-900 text-white" data-id="element-232">
      {/* Hero section */}
      <div className="relative border-b border-gray-800" data-id="element-233">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-234">
          <div className="text-center md:text-left text-white" data-id="element-235">
            <p className="text-indigo-400 text-sm font-semibold tracking-wide uppercase" data-id="element-236">
              Case Study {caseStudy.id}
            </p>
            <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-id="element-237">
              {caseStudy.title}
            </h1>
          </div>
        </div>
      </div>
      {/* Case study content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-238">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-239">
          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-240">
            <div className="sticky top-24" data-id="element-241">
              <h2 className="text-lg font-medium text-white mb-4" data-id="element-242">
                Quick Navigation
              </h2>
              {caseStudy.id === 4 ? <nav className="space-y-1" aria-label="Sidebar" data-id="element-243">
                  <a href="#presentation" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-244">
                    Swiggy Instamart Case Study
                  </a>
                </nav> : caseStudy.id === 1 || caseStudy.id === 2 || caseStudy.id === 3 ? <nav className="space-y-1" aria-label="Sidebar" data-id="element-245">
                  <a href="#product-overview" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-246">
                    Product Overview
                  </a>
                  <a href="#problem-context" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-247">
                    Problem Context
                  </a>
                  <a href="#research-insights" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-248">
                    Research & Insights
                  </a>
                  <a href="#product-goal" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-249">
                    Product Goal
                  </a>
                  <a href="#solution-hypothesis" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-250">
                    Solution Hypothesis
                  </a>
                  {caseStudy.id !== 3 && <a href="#solution-design" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-251">
                      Solution Design
                    </a>}
                  <a href="#solution-walkthrough" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-252">
                    Solution Walkthrough
                  </a>
                  <a href="#product-thinking" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-253">
                    Product Thinking Snapshot
                  </a>
                  <a href="#evaluation-plan" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-254">
                    Evaluation Plan
                  </a>
                  <a href="#reflection" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-255">
                    Reflection
                  </a>
                </nav> : null}
              <div className="mt-8 flex flex-wrap gap-2" data-id="element-256">
                {caseStudy.tags.map((tag, index) => <span key={index} className="bg-gray-800 text-indigo-300 text-xs px-2 py-1 rounded-full border border-gray-700" data-id="element-257">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
          {/* Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-258">
            {caseStudy.id === 4 ? <section id="presentation" className="space-y-6" data-id="element-259">
                <div className="flex items-start" data-id="element-260">
                  <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0" data-id="element-261">
                    <img src={caseStudy.imageUrl} alt={caseStudy.title} className="w-full h-full object-cover" data-id="element-262" />
                  </div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6" data-id="element-263">
                  <p className="text-gray-300 text-lg leading-relaxed" data-id="element-264">
                    Every great product challenge starts with a "why." For this
                    one, my teammate Arpita and I asked — why do users drop off
                    before completing their Instamart purchase? What began as a
                    simple question turned into a deep dive into user journeys,
                    motivations, and missed opportunities. The result? A
                    data-driven strategy to increase transaction volume and make
                    every Instamart visit more seamless and rewarding. You can
                    explore our approach and final recommendations in the
                    presentation below.
                  </p>
                </div>
                <div className="aspect-ratio-container" data-id="element-265">
                  <iframe src="https://pitch.com/embed-link/kq5emf" allow="fullscreen; clipboard-write" allowFullScreen title="Swiggy Instamart Transaction Volume Increase Case Study" className="w-full h-[600px] border-0 rounded-lg shadow-lg" data-id="element-266"></iframe>
                </div>
              </section> : caseStudy.id === 1 || caseStudy.id === 2 || caseStudy.id === 3 ? <>
                <div className="flex items-start" data-id="element-267">
                  <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0" data-id="element-268">
                    <img src={caseStudy.imageUrl} alt={caseStudy.title} className="w-full h-full object-cover" data-id="element-269" />
                  </div>
                </div>
                <section id="product-overview" data-id="element-270">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-271">
                    Product Overview
                  </h2>
                  <p className="text-gray-300 whitespace-pre-line" data-id="element-272">
                    {caseStudy.productOverview.description}
                  </p>
                  <div className="mt-6 space-y-4" data-id="element-273">
                    <div data-id="element-274">
                      <h3 className="text-lg font-medium text-white" data-id="element-275">
                        Market Overview
                      </h3>
                      <p className="text-gray-300" data-id="element-276">
                        {caseStudy.productOverview.marketOverview}
                      </p>
                    </div>
                    <div data-id="element-277">
                      <h3 className="text-lg font-medium text-white" data-id="element-278">
                        Competitors
                      </h3>
                      <p className="text-gray-300" data-id="element-279">
                        {caseStudy.productOverview.competitors}
                      </p>
                    </div>
                    <div data-id="element-280">
                      <h3 className="text-lg font-medium text-white" data-id="element-281">Edge</h3>
                      <p className="text-gray-300" data-id="element-282">
                        {caseStudy.productOverview.edge}
                      </p>
                    </div>
                  </div>
                </section>
                <section id="problem-context" data-id="element-283">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-284">
                    1. Problem Context
                  </h2>
                  <p className="text-gray-300 whitespace-pre-line" data-id="element-285">
                    {caseStudy.problemContext}
                  </p>
                </section>
                <section id="research-insights" data-id="element-286">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-287">
                    2. Research & Insights
                  </h2>
                  {caseStudy.researchInsights && <>
                      <div className="mb-6" data-id="element-288">
                        <h3 className="text-lg font-medium text-indigo-400 mb-3" data-id="element-289">
                          {caseStudy.id === 3 ? 'Research Overview:' : 'What I did:'}
                        </h3>
                        <p className="text-gray-300 whitespace-pre-line" data-id="element-290">
                          {caseStudy.researchInsights.whatIDid}
                        </p>
                      </div>
                      <div data-id="element-291">
                        <h3 className="text-lg font-medium text-indigo-400 mb-3" data-id="element-292">
                          {caseStudy.id === 3 ? 'Key Insights:' : 'What I found:'}
                        </h3>
                        <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-293">
                          {caseStudy.researchInsights.whatIFound.map((finding, index) => <li key={index} data-id="element-294">{finding}</li>)}
                        </ul>
                      </div>
                    </>}
                </section>
                <section id="product-goal" data-id="element-295">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-296">
                    3. Product Goal
                  </h2>
                  {caseStudy.productGoal && <>
                      <p className="text-gray-300 mb-6" data-id="element-297">
                        {caseStudy.productGoal.description}
                      </p>
                      <div data-id="element-298">
                        <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-299">
                          {caseStudy.productGoal.expectedOutcomes.map((outcome, index) => <li key={index} data-id="element-300">{outcome}</li>)}
                        </ul>
                      </div>
                    </>}
                </section>
                <section id="solution-hypothesis" data-id="element-301">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-302">
                    4. Solution Hypothesis
                  </h2>
                  <p className="text-gray-300" data-id="element-303">
                    {caseStudy.solutionHypothesis}
                  </p>
                </section>
                {caseStudy.id !== 3 && <section id="solution-design" data-id="element-304">
                    <h2 className="text-2xl font-bold text-white mb-4" data-id="element-305">
                      5. Solution Design
                    </h2>
                    {caseStudy.solutionDesign && <div className="space-y-8" data-id="element-306">
                        {caseStudy.solutionDesign.map((design, index) => <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-307">
                            <h3 className="text-xl font-bold text-indigo-400 mb-4" data-id="element-308">
                              {design.title}
                            </h3>
                            <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-309">
                              {design.features.map((feature, featureIndex) => <li key={featureIndex} data-id="element-310">{feature}</li>)}
                            </ul>
                          </div>)}
                      </div>}
                  </section>}
                <section id="solution-walkthrough" data-id="element-311">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-312">
                    {caseStudy.id === 3 ? '5. Solution Walkthrough' : '6. Solution Walkthrough'}
                  </h2>
                  {caseStudy.id === 3 && caseStudy.improvements ? <div className="space-y-12" data-id="element-313">
                      {caseStudy.improvements.map((improvement, index) => <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-314">
                          <h3 className="text-xl font-bold text-white mb-3" data-id="element-315">
                            {index + 1}. {improvement.title}
                          </h3>
                          <p className="text-gray-300 whitespace-pre-line mb-6" data-id="element-316">
                            {improvement.description}
                          </p>
                          {improvement.imageUrl && <img src={improvement.imageUrl} alt={improvement.title} className="w-full rounded-lg shadow-md" data-id="element-317" />}
                        </div>)}
                    </div> : caseStudy.solutionWalkthroughVideo ? <div className="aspect-ratio-container rounded-lg overflow-hidden shadow-lg" data-id="element-318">
                      <iframe src={caseStudy.solutionWalkthroughVideo} title={`${caseStudy.company} Solution Walkthrough`} className="w-full h-[400px] border-0" allow="autoplay" data-id="element-319"></iframe>
                    </div> : null}
                </section>
                <section id="product-thinking" data-id="element-320">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-321">
                    {caseStudy.id === 3 ? '6. Product Thinking Snapshot' : '7. Product Thinking Snapshot'}
                  </h2>
                  {caseStudy.productThinkingSnapshot && <div className="space-y-4" data-id="element-322">
                      {caseStudy.productThinkingSnapshot.map((item, index) => <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-id="element-323">
                          <h3 className="text-lg font-semibold text-indigo-400 mb-2" data-id="element-324">
                            {item.title}
                          </h3>
                          <p className="text-gray-300" data-id="element-325">{item.description}</p>
                        </div>)}
                    </div>}
                </section>
                <section id="evaluation-plan" data-id="element-326">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-327">
                    {caseStudy.id === 3 ? '7. Evaluation Plan' : '8. Evaluation Plan'}
                  </h2>
                  {caseStudy.evaluationPlan && <>
                      <p className="text-gray-300 mb-6" data-id="element-328">
                        {caseStudy.evaluationPlan.description}
                      </p>
                      <div data-id="element-329">
                        <h3 className="text-lg font-medium text-indigo-400 mb-3" data-id="element-330">
                          {caseStudy.id === 1 || caseStudy.id === 3 ? 'Validation Approach:' : 'Key Metrics to Evaluate:'}
                        </h3>
                        <div className="space-y-4" data-id="element-331">
                          {caseStudy.evaluationPlan.keyMetrics.map((metric, index) => <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-id="element-332">
                                <h4 className="text-base font-semibold text-white mb-2" data-id="element-333">
                                  {metric.title}
                                </h4>
                                <p className="text-gray-300" data-id="element-334">
                                  {metric.description}
                                </p>
                              </div>)}
                        </div>
                      </div>
                    </>}
                </section>
                <section id="reflection" data-id="element-335">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-336">
                    {caseStudy.id === 3 ? '8. Reflection' : '9. Reflection'}
                  </h2>
                  <div className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-337">
                    <p className="text-indigo-300 font-medium whitespace-pre-line" data-id="element-338">
                      {caseStudy.reflection}
                    </p>
                  </div>
                </section>
              </> : null}
            {/* Navigation buttons */}
            <div className="flex justify-between pt-8 border-t border-gray-700" data-id="element-339">
              {caseStudy.id > 1 ? <Link to={`/case-study/${caseStudy.id - 1}`} className="flex items-center text-indigo-400 hover:text-indigo-300" data-id="element-340">
                  <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-341" />
                  Previous Case Study
                </Link> : <div data-id="element-342"></div>}
              {caseStudy.id < 4 ? <Link to={`/case-study/${caseStudy.id + 1}`} className="flex items-center text-indigo-400 hover:text-indigo-300" data-id="element-343">
                  Next Case Study
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-344" />
                </Link> : <div data-id="element-345"></div>}
            </div>
          </div>
        </div>
      </div>
      {/* CTA section */}
      <div className="bg-gray-800 py-12" data-id="element-346">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-347">
          <div className="text-center" data-id="element-348">
            <h2 className="text-3xl font-bold text-white" data-id="element-349">
              Interested in working together?
            </h2>
            <p className="mt-4 text-lg text-gray-300" data-id="element-350">
              Let's discuss how I can bring my product expertise to your team.
            </p>
            <div className="mt-8" data-id="element-351">
              <Button to="/contact" variant="primary" size="lg" data-id="element-352">
                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default CaseStudy;